import { Injectable, inject } from '@angular/core';
import { ChartType, InspectionType, ProcessType } from '@enums';
import {
  processIncomingInspectionData,
  processInlineInspectionData,
} from '@helpers';
import {
  ApiResponse,
  Article,
  BomNode,
  Company,
  Equipment,
  IncomingOrPeriodicInspection,
  InlineInspection,
  ProcessParameter,
  ProductionNumber,
  SPCGeneralData,
} from '@interfaces';
import { Observable, map, of } from 'rxjs';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class ArticleService {
  private readonly _apiPath = 'articles';
  private readonly _api = inject(ApiService);

  getArticle(
    articleId: string,
    batchIds?: string[] | null,
    rootArticleId?: string | null,
    rootBatchId?: string | null,
  ): Observable<ApiResponse<Article>> {
    return this._api.get<Article>(
      `${this._apiPath}/${articleId}/?batchId=${batchIds?.join(
        '&batchId=',
      )}&rootArticleId=${rootArticleId}&rootBatchId=${rootBatchId}`,
    );
  }

  getBomTree(
    articleId: string | undefined,
    batchId: string | undefined,
    companies: string[],
  ): Observable<ApiResponse<BomNode>> {
    if (!articleId || !batchId) {
      return of({ data: null });
    }
    let url = `${this._apiPath}/${articleId}/batches/${batchId}/bom`;
    if (companies.length > 0) {
      const query = companies
        .map((c) => `companies=${encodeURIComponent(c)}`)
        .join('&');
      url += `?${query}`;
    }
    return this._api.get<BomNode>(url);
  }

  getIncomingInspections(
    inspections: InspectionType,
    articleId: BomNode['articleId'],
    batchIds: BomNode['batchId'][],
    rootArticleId: BomNode['articleId'],
    rootBatchId: BomNode['batchId'],
    // It will probably be used in the future

    companies: string[],
  ): Observable<ApiResponse<IncomingOrPeriodicInspection[]>> {
    let query = `${this._apiPath}/${articleId}/${this.mapInspectionTypeToApiPath(inspections, true)}?batchId=${batchIds?.join('&batchId=')}&rootArticleId=${rootArticleId}&rootBatchId=${rootBatchId}`;

    if (companies.length > 0) {
      query += `&companies=${encodeURIComponent(companies.join(','))}`;
    }
    return this._api.get<IncomingOrPeriodicInspection[]>(query).pipe(
      map((result) => {
        if (!result.data) {
          return { data: null };
        }

        result.data = result.data.map((inspection) =>
          processIncomingInspectionData(
            inspection,
            articleId,
            batchIds,
            rootArticleId,
            rootBatchId,
          ),
        );

        return result;
      }),
    );
  }

  getInlineInspections(
    inspections: InspectionType,
    articleId: BomNode['articleId'],
    batchIds: BomNode['batchId'][],
    rootArticleId: BomNode['articleId'],
    rootBatchId: BomNode['batchId'],
    companies: string[],
    processType?: ProcessType,
  ): Observable<ApiResponse<InlineInspection[]>> {
    const params = {
      batchId: batchIds,
      rootArticleId,
      rootBatchId,
    };

    if (processType) {
      Object.assign(params, { processType });
    }

    return this._api
      .get<InlineInspection[]>(
        `${this._apiPath}/${articleId}/${this.mapInspectionTypeToApiPath(
          inspections,
          true,
        )}`,
        {
          params,
        },
      )
      .pipe(
        map((result) => {
          if (!result.data) {
            return { data: null };
          }

          result.data = result.data.map((inspection) =>
            processInlineInspectionData(
              inspection,
              articleId,
              batchIds,
              rootArticleId,
              rootBatchId,
            ),
          );

          return result;
        }),
      );
  }

  getPeriodicInspections(
    inspections: InspectionType,
    articleId: BomNode['articleId'],
    batchIds: BomNode['batchId'][],
    rootArticleId: BomNode['articleId'],
    rootBatchId: BomNode['batchId'],
    // It will probably be used in the future
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    companies: string[],
  ): Observable<ApiResponse<IncomingOrPeriodicInspection[]>> {
    return this._api
      .get<IncomingOrPeriodicInspection[]>(
        `${this._apiPath}/${articleId}/${this.mapInspectionTypeToApiPath(inspections, true)}?batchId=${batchIds?.join('&batchId=')}&rootArticleId=${rootArticleId}&rootBatchId=${rootBatchId}`,
      )
      .pipe(
        map((result) => {
          if (!result.data) {
            return { data: null };
          }

          result.data = result.data.map((inspection) =>
            processIncomingInspectionData(
              inspection,
              articleId,
              batchIds,
              rootArticleId,
              rootBatchId,
            ),
          );

          return result;
        }),
      );
  }

  getMeasurementsModalData(
    productionOrderId: InlineInspection['productionOrderId'],
    nodeId: ProcessParameter['nodeId'],
    articleId: InlineInspection['articleId'],
    batchId: InlineInspection['batchId'],
    rootBatchId: InlineInspection['rootBatchId'],
    rootArticleId: InlineInspection['rootArticleId'],
    processType: ProcessType,
  ) {
    return this._api.post(
      `production-orders/${productionOrderId}/${processType.toLowerCase()}`,
      {
        nodeId,
        articleId,
        batchId,
        rootBatchId,
        rootArticleId,
      },
    );
  }

  getCompanies(articleId?: string) {
    if (articleId) {
      return this._api.get<Company[]>(
        `${this._apiPath}/${articleId}/companies`,
      );
    } else {
      return this._api.get<Company[]>('companies');
    }
  }

  getCompaniesWithoutArticle() {
    return this._api.get<Company[]>(`${this._apiPath}/companies`);
  }

  getPlannedBom(articleId: string, companyCode: string) {
    return this._api
      .get<ProductionNumber[]>(`${this._apiPath}/${articleId}/planned-bom`, {
        params: { companyCode },
      })
      .pipe(
        map((result) => {
          result.data?.forEach((productionNumber) => {
            productionNumber.derivedPeakPercentageDemand =
              productionNumber.peakPercentageDemand * 100 + '%';

            productionNumber.stockAvailable =
              productionNumber.stockAvailable ?? '-';

            productionNumber.cycleStock = productionNumber.cycleStock ?? '-';
            productionNumber.outputPerDay =
              productionNumber.outputPerDay ?? '-';
            productionNumber.dosagePerDay =
              productionNumber.dosagePerDay ?? '-';
          });

          return result;
        }),
      );
  }

  mapInspectionTypeToApiPath(inspectionType: InspectionType, plural = false) {
    switch (inspectionType) {
      case InspectionType.Incoming:
        return 'incoming-inspection' + (plural ? 's' : '');
      case InspectionType.Inline:
        return 'inline-inspection' + (plural ? 's' : '');
      case InspectionType.Periodic:
        return 'periodic-inspection' + (plural ? 's' : '');
    }
  }

  getSPCGeneralData({
    articleId,
    rootArticleId,
    rootBatchId,
    batchId,
    parameter,
    processType,
    inspectionType,
  }: {
    articleId: string;
    batchId: string;
    rootArticleId: string;
    rootBatchId: string;
    parameter: string;
    processType: string;
    inspectionType: InspectionType;
  }) {
    return this._api.post<
      SPCGeneralData,
      {
        rootArticleId: string;
        rootBatchId: string;
        batchId: string;
        parameter: string;
        processType: string;
      }
    >(
      `${this._apiPath}/${articleId}/spc/${this.mapInspectionTypeToApiPath(
        inspectionType,
      )}`,
      {
        rootArticleId,
        rootBatchId,
        batchId,
        parameter,
        processType,
      },
    );
  }

  getSPCChartsData({
    articleId,
    rootArticleId,
    rootBatchId,
    batchId,
    parameter,
    processType,
    subgroupSize,
    samplingInterval,
    numberOfGroups,
    productionOrderId,
    inspectionType,
    chartType,
    equipmentIds,
    isIntervalSampling,
    start,
    end,
  }: {
    articleId: string;
    batchId: string;
    rootArticleId: string;
    rootBatchId: string;
    parameter: string | null;
    processType: string;
    subgroupSize: number;
    samplingInterval?: string | null;
    numberOfGroups: number;
    productionOrderId?: string;
    inspectionType: InspectionType;
    chartType: ChartType;
    equipmentIds?: string[] | null;
    isIntervalSampling?: boolean;
    start: string;
    end: string;
  }) {
    if (equipmentIds?.length === 1 && equipmentIds[0] === '') {
      equipmentIds = null;
    }

    const params = {
      rootArticleId,
      rootBatchId,
      batchId,
      parameter,
      processType,
      subgroupSize,
      samplingInterval,
      productionOrderId,
      numberOfGroups,
      chartType,
      equipmentIds,
      isIntervalSampling,
      start,
      end,
    };

    if (!samplingInterval) {
      delete params.samplingInterval;
    }

    if (productionOrderId) {
      Object.assign(params, { productionOrderId });
    }

    return this._api.post(
      `${this._apiPath}/${articleId}/spc/${this.mapInspectionTypeToApiPath(
        inspectionType,
      )}/spc-charts`,
      params,
    );
  }

  getCharacteristics(
    articleId: string,
    batchId: string,
    rootArticleId: string,
    rootBatchId: string,
    inspectionType: InspectionType,
  ) {
    return this._api.get<{
      batchId: string;
      processType: string;
      characteristics: { id: string; name: string }[];
    }>(
      `${this._apiPath}/${articleId}/should-value/${batchId}/characteristics`,
      {
        params: {
          articleId,
          batchId,
          rootArticleId,
          rootBatchId,
          inspectionType,
        },
      },
    );
  }

  getEquipments(
    articleId: string,
    batchId: string,
    rootArticleId: string,
    rootBatchId: string,
    nodeId: string,
  ) {
    return this._api.post<
      Equipment[],
      { rootArticleId: string; rootBatchId: string; nodeId: string }
    >(`${this._apiPath}/${articleId}/should-value/${batchId}/equipments`, {
      rootArticleId,
      rootBatchId,
      nodeId,
    });
  }
}
